import moment from 'moment';
import { IDocumentResource } from '@/api/resources/types/document-resource';
import { allAcceptedFileTypes } from '@/common/accepted-file-types';
import { ERecipientRole } from '@/dealroom/recipients/types/enums/recipient-role';
import { EArrangeDirection } from '@/dealroom/types/enums/arrange-pages';
import { EDocumentPageType } from '@/enums/document-page-type';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';

export class DealroomHelper {
  static relativeDate(date: string): string {
    return moment(date).fromNow();
  }

  static isValidUrl(url: string): boolean {
    const urlValidator = new RegExp(
      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/
    );
    return urlValidator.test(url.toLowerCase());
  }

  static extractFileType(fileName: string): string {
    const lastDotIndex: number = fileName.lastIndexOf('.');
    if (lastDotIndex < 0) {
      return null;
    }
    return fileName.substring(lastDotIndex);
  }

  static validFileType(fileType: string): boolean {
    if (!fileType) {
      return false;
    }
    return allAcceptedFileTypes.includes(fileType);
  }

  static removeExtension(filename: string): string {
    return filename.substring(0, filename.lastIndexOf('.'));
  }

  static replaceDocumentPage(
    statePages: IDocumentPage[],
    documentPage: IDocumentPage
  ): IDocumentPage[] {
    if (!statePages || !statePages.length) {
      return [documentPage];
    }
    const pageExists: boolean = statePages.some(
      (page: IDocumentPage) => page.id === documentPage.id
    );
    if (pageExists) {
      return statePages.map((page: IDocumentPage) =>
        page.id === documentPage.id ? documentPage : page
      );
    }
    return this.addDocumentPages(statePages, [documentPage]);
  }

  static addDocumentPages(pages: IDocumentPage[], statePages: IDocumentPage[]): IDocumentPage[] {
    if (statePages.length === 0) {
      return this.correctPageIndexes(pages);
    }
    const updatedPages: IDocumentPage[] = [...statePages];
    updatedPages.splice(pages[0].pageNum - 1, 0, ...pages);
    return this.correctPageIndexes(updatedPages);
  }

  static removeDocumentPage(pageId: string, statePages: IDocumentPage[]): IDocumentPage[] {
    const updatedPages: IDocumentPage[] = statePages.filter(
      (statePage: IDocumentPage) => statePage.id !== pageId
    );
    return this.correctPageIndexes(updatedPages);
  }

  private static correctPageIndexes(pages: IDocumentPage[]): IDocumentPage[] {
    if (!pages || pages.length == 0) {
      return pages;
    }
    const updatedPages: IDocumentPage[] = pages.map((page: IDocumentPage, index: number) => ({
      ...page,
      pageNum: index + 1,
    }));
    return updatedPages.sort(
      (pageA: IDocumentPage, pageB: IDocumentPage) => pageA.pageNum - pageB.pageNum
    );
  }

  static rearrangePages(
    pages: IDocumentPage[],
    movedPage: IDocumentPage,
    arrangeDirection: EArrangeDirection
  ): IDocumentPage[] {
    const newPages: IDocumentPage[] = pages.filter(
      (page: IDocumentPage) => page.id !== movedPage.id
    );
    if (arrangeDirection === EArrangeDirection.Up && movedPage.pageNum === 1) {
      return this.correctPageIndexes([movedPage, ...newPages]);
    }
    if (arrangeDirection === EArrangeDirection.Down && movedPage.pageNum === pages.length) {
      return this.correctPageIndexes([...newPages, movedPage]);
    }
    newPages.splice(movedPage.pageNum - 1, 0, movedPage);
    return this.correctPageIndexes(this.correctPageIndexes(newPages));
  }

  static signableRecipients(recipients: IRecipient[]) {
    return recipients.filter((recipient: IRecipient) =>
      [ERecipientRole.Approver, ERecipientRole.Signer].includes(recipient.role as ERecipientRole)
    );
  }

  static getCurrentDocumentPageAfterPageDelete(
    deletedPageIndex: number,
    pages: IDocumentPage[]
  ): IDocumentPage {
    let nextCurrentDocumentPage: IDocumentPage = pages[deletedPageIndex];
    if (!nextCurrentDocumentPage) {
      nextCurrentDocumentPage = pages[deletedPageIndex - 1];
    }
    return nextCurrentDocumentPage;
  }

  static updatePageNums(
    pages: IDocumentPage[],
    newPageNums: Partial<IDocumentPage>[]
  ): IDocumentPage[] {
    return pages
      .map((page: IDocumentPage) => ({
        ...page,
        pageNum: newPageNums.find(pageNum => pageNum.id === page.id).pageNum,
      }))
      .sort((a: IDocumentPage, b: IDocumentPage): number => a.pageNum - b.pageNum);
  }

  static mapDocumentResources(
    pages: IDocumentPage[],
    documentResources: IDocumentResource[]
  ): IDocumentPage[] {
    return pages.map((page: IDocumentPage) => {
      let resourceId = '';
      switch (page.pageType) {
        case EDocumentPageType.Editor: {
          if (!page.editorBlock) {
            break;
          }
          resourceId = DealroomHelper.findResourceId(documentResources, page.editorBlock.id);
          break;
        }
        case EDocumentPageType.ContentLink: {
          if (!page.contentLinkBlock) {
            break;
          }
          resourceId = DealroomHelper.findResourceId(documentResources, page.contentLinkBlock.id);
          break;
        }
        case EDocumentPageType.Video: {
          if (!page.video) {
            break;
          }
          resourceId = DealroomHelper.findResourceId(documentResources, page.video.id);
          break;
        }
        default:
          break;
      }
      return { ...page, resourceId };
    });
  }

  static sortRecipients(recipients: IRecipient[]): IRecipient[] {
    return recipients.sort((a: IRecipient, b: IRecipient) => {
      if (a.role === ERecipientRole.Approver) {
        return -1;
      }
      if (b.role !== ERecipientRole.Approver) {
        return 1;
      }
    });
  }

  static orderRecipients(recipients: IRecipient[]): Partial<IRecipient>[] {
    return recipients.map((recipient, i) => ({ id: recipient.id, orderNum: i + 1 }));
  }

  static findResourceId(resources: IDocumentResource[], blockId: string): string {
    return resources.find((resource: IDocumentResource) => resource.contentId === blockId)
      ?.resourceId;
  }

  static updatePage(pages: IDocumentPage[], updatedPage: IDocumentPage): IDocumentPage[] {
    return pages.map((page: IDocumentPage) => (page.id === updatedPage.id ? updatedPage : page));
  }
}
